import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import _ from 'lodash';
import { Button, Pagination, Form, Dropdown, DropdownButton } from 'react-bootstrap';
import Confirm from '../../../components/Modal/Confirm';
import DOMPurify from 'dompurify';

import Loading from '../../../components/Loading/Loading';
import throttle from '../../../utils/throttle';
import pagination from '../../../utils/pagination';
import { getNotifications, updateNotification, createNotification, removeNotification } from '../NotificationsActions';
import NotificationConfig from './NotificationConfig';

import settingIcon from '../../../assets/images/settings-gray-icon.svg';
import deleteIcon from '../../../assets/images/delete-icon.svg';

import '../../../assets/styles/_table.scss';
import './NotificationsTable.scss';

class NotificationsTable extends Component {
  static propTypes = {
    pagination: PropTypes.bool,
    show: PropTypes.number,
  };

  static defaultProps = {
    pagination: true,
    show: 10,
  };

  constructor(props) {
    super(props);

    this.state = {
      page: 1,
      dateFormat: 'DD MMM YYYY HH:mm',
      search: '',
      sort: '-from',
      sortOptions: [
        { name: 'Start date: newest first', value: '-from' },
        { name: 'Start date: oldest first', value: 'from' },
        { name: 'Name: A to Z', value: 'name' },
        { name: 'Name: Z to A', value: '-name' },
      ],
      sortDir: 'desc',
    };

    this.getNotificationsThrottled = throttle(this.getNotifications, 500, this);
  }

  componentDidMount() {
    this.getNotifications();
  }

  handleChange = (ev) => {
    this.setState({ search: ev.target.value, page: 1 });
    this.getNotificationsThrottled();
  };

  goto = (page = 1) => {
    if (page < 1) {
      page = 1;
    }

    this.setState({ page }, this.getNotifications);
  };

  getNotifications = () => {
    const { search, page, sort } = this.state;
    this.props.getNotifications({
      search,
      page,
      sort,
    });
  };

  applySort = (sort) => {
    this.setState({ sort }, this.getNotifications);
  };

  toggleSort = () => {
    this.setState(
      (prev) => {
        const sortDir = prev.sortDir === 'asc' ? 'desc' : 'asc';
        return { sortDir, sort: sortDir === 'asc' ? 'name' : '-name', page: 1 };
      },
      this.getNotifications
    );
  };

  getAudience(id) {
    switch (id) {
      case '1':
        return 'Has billing access';
      case '2':
        return 'Has monthly plan';
      case '3':
        return 'Has annual plan';
      case '4':
        return 'Trials';
      case '5':
        return 'Enrolled in SP';
      case '6': 
        return 'Actual billing plan';
      default:
        return 'All users';
    }
  }

  getAvailability(from, to, enabled) {
    const date = +new Date();
    if (!enabled) return ['red', 'Offline'];
    if (date < from || date > to) return ['red', 'Offline'];
    return ['green', 'Online'];
  }

  toggleNotification(_id) {
    const notification = _.find(this.props.results, { _id });
    if (!notification) return;
    const model = {
      ...notification,
      enabled: !notification.enabled,
    };
    this.props.updateNotification(model);
  }

  renderSelected = (type) => {
      const selected = _.find(this.state[type + 'Options'], {
        value: this.state[type],
      });
      return selected ? selected.name : '';
    };

  renderPagination = () => {
    const pages = pagination(this.props.total, this.props.page, 10, 5);

    if (pages.totalPages < 2) {
      return <div></div>;
    }
    return (
      <Pagination>
        {pages.currentPage > 1 && <Pagination.First onClick={() => this.goto(1)} />}
        {pages.currentPage > 1 && <Pagination.Prev onClick={() => this.goto(pages.currentPage - 1)} />}
        {pages.pages.map((p, i) => {
          return (
            <Pagination.Item key={i} active={p === pages.currentPage} onClick={() => this.goto(p)}>
              {p}
            </Pagination.Item>
          );
        })}
        {pages.currentPage < pages.totalPages && <Pagination.Next onClick={() => this.goto(pages.currentPage + 1)} />}
        {pages.currentPage < pages.totalPages && <Pagination.Last onClick={() => this.goto(pages.totalPages)} />}
      </Pagination>
    );
  };

  renderRow = (data, i) => {
    const { dateFormat } = this.state;

    const [timeRangeClass, timeRangeLabel] = this.getAvailability(data.from, data.to, data.enabled);

    const formatted = data.description
      .replace(/rel="noopener noreferrer"/gm, 'rel="noopener noreferrer" target="_blank"')
      .replace(/<p>/gm, '<span>')
      .replace(/<\/p>/gm, '</span>');

    const sanitized = DOMPurify.sanitize(formatted, { FORCE_BODY: true, ADD_ATTR: ['target'] });

    return (
      <tr key={i}>
        <td><p>{data.name}</p></td>
        <td><p className='description-content' dangerouslySetInnerHTML={{ __html: sanitized }}></p></td>
        <td><p className='w-80'>{moment(data.from).format(dateFormat)}</p></td>
        <td><p className='w-80'>{data.to && moment(data.to).format(dateFormat)}</p></td>
        <td><p className={`status-box ${timeRangeClass}`}>{timeRangeLabel}</p></td>
        <td style={{width: '465px'}}>
          <div>
            {_.map(data.audience, (value, key) => (
              <span className={`audience ${value === true ? 'include' : 'exclude'}`} key={key}>
                {this.getAudience(key)}
              </span>
            ))}
          </div>
        </td>
        <td style={{width: '136px'}}>
          <div className='action-btn'>
            <Dropdown className="dropdown-settings-menu accounts">
              <Dropdown.Toggle className="btn-menu">
                <img src={settingIcon} alt="#" />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {data.enabled && (
                  <Dropdown.Item>
                    <Confirm
                      body={
                        <p>
                          Are you sure you want to disable <br />
                          this notification?
                        </p>
                      }
                      confirmText="Disable"
                      icon={settingIcon}
                      title="Disable notification"
                      onConfirm={() => this.toggleNotification(data._id)}
                    >
                      <Button variant='link'>Disable</Button>
                    </Confirm>
                  </Dropdown.Item>
                )}

                <Dropdown.Item>
                  <NotificationConfig model={data} onClose={this.getNotifications}>
                    <Button variant='link'>Edit</Button>
                  </NotificationConfig>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            <Confirm
              body={
                <p>
                  Are you sure you want to remove <br />
                  this notification?
                </p>
              }
              confirmText="Remove"
              icon={deleteIcon}
              title="Remove notification"
              onConfirm={() => this.props.removeNotification(data._id)}
            >
              <Button variant='delete' className='btn-icon btn-md'><img src={deleteIcon} alt='#' /></Button>
            </Confirm>
          </div>
        </td>
      </tr>
    );
  };

  onClose() {}

  handleInput() {}

  handleSubmit() {}

  render() {
    const { isLoading, results, total } = this.props;
    const { sortDir } = this.state;

    return (
      <>
        <div className='table-content'>
          <div className='table-filter'>
            <h2>Notifications</h2>
            <div className='filter-content' id="filter-content">
              <Form.Group controlId="sort">
                <DropdownButton className="form-dropdown sort" title={this.renderSelected('sort')}>
                  {this.state.sortOptions.map((s, i) => {
                    return (
                      <Dropdown.Item
                        key={i}
                        as="button"
                        className={this.state.sort === s.value ? 'active' : ''}
                        onClick={() => this.applySort(s.value)}>
                        {s.name}
                      </Dropdown.Item>
                    );
                  })}
                </DropdownButton>
              </Form.Group>

              <Form.Group controlId="Search" className="search-form">
                <Form.Control
                  type="text"
                  name="search"
                  placeholder="Search"
                  onChange={this.handleChange}
                  value={this.state.search}
                />
              </Form.Group>
            </div>
          </div>

          {isLoading && (
            <div className='table-loading'>
              <Loading />
            </div>
          )}
          {!isLoading && total === 0 && (
            <div className='no_data'>
              <p> No data available </p>
            </div>
          )}

          <div className='table'>
            <table>
              <thead>
                <tr>
                  <th onClick={this.toggleSort} className='sortable'>
                    Name <span className='sort-icon'>{sortDir === 'asc' ? '↓' : '↑'}</span>
                  </th>
                  <th>Description</th>
                  <th>Starts on</th>
                  <th>Ends on</th>
                  <th>Status</th>
                  <th>Audience</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {!isLoading && results && _.map(results, (data, i) => this.renderRow(data, i))}
              </tbody>
            </table>
          </div>
        </div>
        {total > 0 && this.renderPagination()}
      </>
    );
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    getNotifications: (opt) => dispatch(getNotifications(opt)),
    createNotification: (model) => dispatch(createNotification(model)),
    removeNotification: (id) => dispatch(removeNotification(id)),
    updateNotification: (model) => dispatch(updateNotification(model)),
  };
};

function mapStateToProps(state) {
  return {
    results: state.notifications.results,
    isLoading: state.notifications.isLoading,
    page: state.notifications.page,
    total: state.notifications.total,
    user: state.auth.user,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(NotificationsTable);
